import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Box } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled || open ? "bg-crate-black/90 backdrop-blur-md border-b border-white/10" : "bg-transparent"}`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-crate-yellow text-crate-black">
            <Box className="h-6 w-6" strokeWidth={2.5} />
          </span>
          <span className="font-heading font-extrabold text-lg leading-none text-white">
            Color Crate<span className="text-crate-yellow">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${pathname === l.to ? "text-crate-yellow" : "text-white/70 hover:text-white"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/reserve"
            data-testid="nav-reserve-btn"
            className="rounded-full bg-crate-yellow text-crate-black font-bold px-6 py-2.5 text-sm hover:bg-white transition-colors"
          >
            Reserve Totes
          </Link>
        </div>

        <button className="md:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-crate-black border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="py-3 text-white/80 font-semibold border-b border-white/5" data-testid={`nav-mobile-${l.label.toLowerCase()}`}>
                  {l.label}
                </Link>
              ))}
              <Link to="/reserve" className="mt-3 rounded-full bg-crate-yellow text-crate-black font-bold px-6 py-3 text-center" data-testid="nav-mobile-reserve">
                Reserve Totes
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
